import type { Maybe } from '@amazon/vinyl-util'
import type { CreateCloudQueuePlayOptions } from '@/cloudQueue/cloudQueueService'
import { isExplicitLanguageFilterOn } from '@/explicitLanguageFilter/explicitLanguageFilterService'
import type { PlaylistEntry } from '@/playback/PlaylistController'
import { playableEntityToPlaylistEntry } from '@/playback/playableEntityToPlaylistEntry'
import { selectPlayerKind } from '@/playback/selectPlayerKind'
import { isExplicitEntry } from '@/playback/skipGuard'
import type { PlayableEntity } from '@/service/fragments/playableEntityFrag'
import type { PlayParams } from '@/service/fragments/playParamsFrag'
import { catalogizeLibraryAsinMrn, MrnAuthority } from '@/util/mrn'

function offersPlay(playParams: Maybe<Pick<PlayParams, 'selections'>>): boolean {
    return playParams?.selections?.includes('PLAY') === true
}

// A library page's owned track names the catalog copy, but its play params send the queue to the
// owned one — which streams from the library, not the catalog.
function playsOwnedCopy(playParams: Maybe<Pick<PlayParams, 'id'>>): boolean {
    const id = playParams?.id
    return id != null && catalogizeLibraryAsinMrn(id).startsWith(`mrn:1.0:${MrnAuthority.LIBRARY}:`)
}

/**
 * The entry to start playing before the cloud queue answers, as GH's playback acceleration
 * does, or null to wait for the queue. Only an on-demand play of a catalog track starts
 * early: a shuffle, a station or an entitlement without on-demand play lets the queue pick
 * the track, an owned-only track streams from the library, and a podcast, video or
 * audiobook resolves its start (resume offset, stage, engine) from queue data the eager
 * entry lacks.
 */
export function eagerStartEntry(
    entity: Maybe<PlayableEntity>,
    playParams: PlayParams,
    options: Maybe<CreateCloudQueuePlayOptions>
): PlaylistEntry | null {
    if (!entity || options?.shuffle === 'SHUFFLE_ON' || options?.startAsStation) return null
    if (playParams.selections != null && !offersPlay(playParams)) return null
    if (!offersPlay(entity.playParams) || playsOwnedCopy(entity.playParams)) return null
    const entry = playableEntityToPlaylistEntry(entity)
    if (!entry || selectPlayerKind(entry) !== 'vinylCatalog') return null
    if (isExplicitLanguageFilterOn() && isExplicitEntry(entry)) return null
    return entry
}

function loadUri(entry: PlaylistEntry): string | null {
    const options = entry.trackLoadOptions
    return typeof options === 'function' ? null : options.uri
}

/**
 * Whether the queue's `seed` is the media `eager` started, so that playback can carry on:
 * the same track can still stream from another source (an owned copy instead of catalog).
 */
export function playsSameMedia(eager: PlaylistEntry, seed: PlaylistEntry): boolean {
    return seed.id === eager.id && loadUri(seed) === loadUri(eager)
}
